import React from 'react';
import { alpha, Box, Button, Paper, Stack, Typography } from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Opacity,
  Search,
} from '@mui/icons-material';

const ingredients = [
  {
    name: 'Milk (Cow)',
    quantity: '78.500',
    unit: '%',
    cost: '1.15',
  },
  {
    name: 'Canada blueberry',
    quantity: '10.000',
    unit: '%',
    cost: '6.80',
  },
  {
    name: 'Whole Milk Powder',
    quantity: '7.000',
    unit: '%',
    cost: '4.90',
  },
  {
    name: 'Yogurt',
    quantity: '4.100',
    unit: '%',
    cost: '2.10',
  },
  {
    name: 'Pectin',
    quantity: '0.400',
    unit: '%',
    cost: '9.50',
  },
];

function FormulaCompositionVisual() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 330,
        p: 2.5,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 16px 32px rgba(15, 23, 42, 0.05)',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Ingredients
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25 }}
            >
              Greek Style Yogurt
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add sx={{ fontSize: 15 }} />}
              sx={{
                height: 30,
                fontSize: 11,
                fontWeight: 800,
                px: 1.25,
              }}
            >
              Add Ingredient
            </Button>

            <Box
              sx={{
                width: 34,
                height: 30,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <Search sx={{ fontSize: 16 }} />
            </Box>
          </Stack>
        </Stack>

        {/* Table header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.8fr 0.8fr 0.45fr 0.65fr 0.6fr',
            gap: 1,
            px: 2,
            py: 1,
            bgcolor: alpha('#0F172A', 0.025),
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {['Ingredient', 'Quantity', 'Unit', 'Cost', ''].map((header) => (
            <Typography
              key={header}
              variant="caption"
              sx={{
                fontWeight: 800,
                color: 'text.secondary',
                fontSize: 10.5,
              }}
            >
              {header}
            </Typography>
          ))}
        </Box>

        {/* Rows */}
        {ingredients.map((ingredient, index) => (
          <Box
            key={ingredient.name}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1.8fr 0.8fr 0.45fr 0.65fr 0.6fr',
              gap: 1,
              alignItems: 'center',
              px: 2,
              py: 0.85,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor:
                index === 1
                  ? (theme) => alpha(theme.palette.primary.main, 0.025)
                  : 'background.paper',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: (theme) => alpha(theme.palette.text.primary, 0.06),
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Opacity sx={{ fontSize: 13 }} />
              </Box>

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {ingredient.name}
              </Typography>
            </Stack>

            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: 'primary.main',
              }}
            >
              {ingredient.quantity}
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              {ingredient.unit}
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              {ingredient.cost}
            </Typography>

            <Stack direction="row" spacing={0.75} justifyContent="flex-end">
              <Edit sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Delete sx={{ fontSize: 14, color: 'text.secondary' }} />
            </Stack>
          </Box>
        ))}

        {/* Total row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.8fr 0.8fr 0.45fr 0.65fr 0.6fr',
            gap: 1,
            alignItems: 'center',
            px: 2,
            py: 0.9,
            bgcolor: alpha('#0F172A', 0.025),
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
            Total
          </Typography>

          <Typography variant="caption" sx={{ fontWeight: 900 }}>
            100
          </Typography>

          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
            %
          </Typography>

          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
            2.0500 €
          </Typography>

          <Box />
        </Box>

      </Paper>
    </Box>
  );
}

export default FormulaCompositionVisual;